import { useEffect,  useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const Auth = () => {
	const navigate = useNavigate();
	const { login, register, forgotPassword, loading, isAuthenticated } = useAuth();

	// Estado dos formulários
	const [loginData, setLoginData] = useState({ 
		email: "", 
		password: "" 
	});
	const [registerData, setRegisterData] = useState({
		nome: "",
		email: "",
		phone: "",
		password: "",
		password_confirmation: ""
	});
	const [forgotEmail, setForgotEmail] = useState("");
	const [confirmEmail, setConfirmEmail] = useState(false);
  	const [showForgotPassword, setShowForgotPassword] = useState(false);
  	const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  	const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  	const [showPassword, setShowPassword] = useState(false);

  	// Redireciona se já estiver autenticado.
  	useEffect(() => {
  		if(isAuthenticated) {
  			navigate("/games", { replace: true });
  		}
  	}, [isAuthenticated, navigate]);

  	const handleLogin = async (e: React.FormEvent) => {
  		e.preventDefault();
  		try {
  			await login(loginData);
  		} catch(error) {
  			// console.log(error);
  		}
  	};

  	const handleRegister = async (e: React.FormEvent) => {
  		e.preventDefault();
  		try {
  			await register(registerData);
  		} catch (error) {
  			// console.log(error);
  		}
  	};

  	const handleConfirmEmail = async (e: React.FormEvent) => {
  		e.preventDefault();
  	};

  	const handleForgotPassword = async (e: React.FormEvent) => {
  		e.preventDefault();
  		try {
  			await forgotPassword(forgotEmail);
  		} catch (error) {
  			// Erros já são tratados no AuthContext
  		}
  	};

	return (
	    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
	      <div className="w-full max-w-md">
	        <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors">
	          <ArrowLeft className="mr-2 h-4 w-4" />
	          Voltar ao início
	        </Link>

	        <div className="mb-8 text-center">
	          <div className="flex h-16 w-16 items-center justify-center rounded-2xl gradient-primary shadow-glow mx-auto mb-4 overflow-hidden">
	            <img src="/favtransparent.png" alt="Logo" className="h-16 w-16 object-contain rounded-full" />
	          </div>
	          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary-light bg-clip-text text-transparent">
	            PurpleBet
	          </h1>
	        </div>

	        <Tabs defaultValue="login" className="w-full">
	          <TabsList className="grid w-full grid-cols-2 mb-6">
	            <TabsTrigger value="login">Entrar</TabsTrigger>
	            <TabsTrigger value="signup">Cadastrar</TabsTrigger>
	          </TabsList>

	          <TabsContent value="login">
	            <Card>
	              <CardHeader>
	                <CardTitle>Bem-vindo de volta</CardTitle>
	                <CardDescription>
	                  Entre com suas credenciais para continuar
	                </CardDescription>
	              </CardHeader>
	              <CardContent>
	                {showForgotPassword ? (
	                  <form onSubmit={handleForgotPassword} className="space-y-4">
	                    <div className="space-y-2">
	                      <Label htmlFor="forgot-email">Email</Label>
	                      <Input
	                        id="forgot-email"
	                        type="email"
	                        placeholder="seu@email.com"
	                        value={forgotEmail}
	                        onChange={(e) => setForgotEmail(e.target.value)}
	                        required
	                      />
	                    </div>
	                    <Button
	                      type="submit"
	                      className="w-full gradient-primary"
	                      disabled={loading}
	                    >
	                      {loading ? "Enviando..." : "Enviar email de recuperação"}
	                    </Button>
	                    <Button
	                      type="button"
	                      variant="ghost"
	                      className="w-full"
	                      onClick={() => setShowForgotPassword(false)}
	                    >
	                      Voltar ao login
	                    </Button>
	                  </form>
	                ) : (
	                  <form onSubmit={handleLogin} className="space-y-4">
	                    <div className="space-y-2">
	                      <Label htmlFor="email">Email</Label>
	                      <Input
	                        id="email"
	                        type="email"
	                        placeholder="seu@email.com"
	                        value={loginData.email}
	                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
	                        required
	                      />
	                    </div>
	                    <div className="space-y-2">
	                      <Label htmlFor="password">Senha</Label>
	                      <div className="relative">
	                        <Input
	                          id="password"
	                          type={showPasswordLogin ? "text" : "password"}
	                          placeholder="••••••••"
	                          value={loginData.password}
	                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
	                          required
	                        />
	                        <Button
	                          type="button"
	                          variant="ghost"
	                          size="icon"
	                          className="absolute top-1/2 right-2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
	                          onClick={() => setShowPasswordLogin(!showPasswordLogin)}
	                        >
	                          {showPasswordLogin ? <EyeOff size={16} /> : <Eye size={16} />}
	                        </Button>
	                      </div>
	                    </div>
	                    <Button
	                      type="submit"
	                      className="w-full gradient-primary"
	                      disabled={loading}
	                    >
	                      {loading ? "Entrando..." : "Entrar"}
	                    </Button>
	                  </form>
	                )}
	              </CardContent>
	              {!showForgotPassword && (
	                <CardFooter className="flex flex-col gap-2">
	                  <button
	                    type="button"
	                    onClick={() => setShowForgotPassword(true)}
	                    className="text-sm text-primary hover:underline"
	                  >
	                    Esqueceu sua senha?
	                  </button>
	                </CardFooter>
	              )}
	            </Card>
	          </TabsContent>

	          <TabsContent value="signup">
	            <Card>
	              <CardHeader>
	                <CardTitle>Criar conta</CardTitle>
	                <CardDescription>
	                  Cadastre-se para começar a jogar
	                </CardDescription>
	              </CardHeader>
	              <CardContent>
	                <form onSubmit={handleRegister} className="space-y-4">
	                  <div className="space-y-2">
	                    <Label htmlFor="signup-name">Nome completo</Label>
	                    <Input
	                      id="signup-name"
	                      type="text"
	                      placeholder="Seu nome"
	                      value={registerData.nome}
	                      onChange={(e) => setRegisterData({ ...registerData, nome: e.target.value })}
	                      required
	                    />
	                  </div>
	                  <div className="space-y-2">
	                    <Label htmlFor="signup-email">Email</Label>
	                    <Input
	                      id="signup-email"
	                      type="email"
	                      placeholder="seu@email.com"
	                      value={registerData.email}
	                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
	                      required
	                    />
	                  </div>
	                  <div className="space-y-2">
	                    <Label htmlFor="signup-phone">Phone</Label>
	                    <Input
	                      id="signup-phone"
	                      type="phone"
	                      placeholder="12 3456 78909"
	                      value={registerData.phone}
	                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
	                      required
	                    />
	                  </div>
	                  <div className="space-y-2">
	                    <Label htmlFor="signup-password">Senha</Label>
	                    <div className="relative">
	                      <Input
	                        id="signup-password"
	                        type={showPassword ? "text" : "password"}
	                        placeholder="••••••••"
	                        value={registerData.password}
	                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
	                        required
	                        minLength={6}
	                      />
	                      <Button
	                        type="button"
	                        variant="ghost"
	                        size="icon"
	                        className="absolute top-1/2 right-2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
	                        onClick={() => setShowPassword(!showPassword)}
	                      >
	                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
	                      </Button>
	                    </div>
	                  </div>
	                  <div className="space-y-2">
	                    <Label htmlFor="signup-confirm">Confirmar senha</Label>
	                    <div className="relative">
	                      <Input
	                        id="signup-confirm"
	                        type={showPasswordConfirm ? "text" : "password"}
	                        placeholder="••••••••"
	                        value={registerData.password_confirmation}
	                        onChange={(e) => setRegisterData({ ...registerData, password_confirmation: e.target.value })}
	                        required
	                        minLength={6}
	                      />
	                      <Button
	                        type="button"
	                        variant="ghost"
	                        size="icon"
	                        className="absolute top-1/2 right-2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
	                        onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
	                      >
	                        {showPasswordConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
	                      </Button>
	                    </div>
	                  </div>
	                  <Button
	                    type="submit"
	                    className="w-full gradient-primary"
	                    disabled={loading}
	                  >
	                    {loading ? "Cadastrando..." : "Cadastrar"}
	                  </Button>
	                </form>
	              </CardContent>
	              <CardFooter className="text-xs text-center text-muted-foreground">
	                Ao cadastrar-se, você concorda com nossos Termos de Uso e Política de Privacidade
	              </CardFooter>
	            </Card>
	          </TabsContent>
	        </Tabs>
	      </div>
	    </div>
	);
}

export default Auth;